import { Component, input, inject, computed, effect, signal, output } from '@angular/core';
import { SpecLot } from '../../../models/build-spec-models.models';
import { GenericDropdownComponent, DropdownData } from '@zayan-erp/shared';
import { ElevationService, Elevation } from '@zayan-erp/home-building';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { of, startWith, map } from 'rxjs';
import { SalesService } from '../../../services/sales.service';
import { Router, ActivatedRoute } from '@angular/router';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-model-elevation-tab',
  imports: [GenericDropdownComponent, ReactiveFormsModule, DatePipe],
  templateUrl: './model-elevation-tab.component.html'
})
export class ModelElevationTabComponent {
  readonly specLot = input<SpecLot | null>(null);
  readonly isActive = input<boolean>(false);
  readonly saved = output<void>();
  readonly modelSelected = output<string | null>();
  readonly elevationSelected = output<string | null>();
  readonly #salesService = inject(SalesService);
  readonly #elevationService = inject(ElevationService);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly modelElevationForm = new FormGroup({
    model: new FormControl<string | null>(null),
    elevation: new FormControl<string | null>(null)
  });
  readonly modelControl = this.modelElevationForm.get('model') as FormControl<string | null>;
  readonly elevationControl = this.modelElevationForm.get('elevation') as FormControl<string | null>;
  readonly modelsResource = rxResourceFixed({
    params: () => ({ specLotId: this.specLot()?.id }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.specLotId) {
        return of([]);
      }
      return this.#salesService.readModelsBySpecLot(params.specLotId);
    }
  });
  readonly modelsDropdownData = computed(() => this.modelsResource.value() ?? []);
  readonly selectedModelId = toSignal(this.modelControl.valueChanges.pipe(startWith(this.modelControl.value)), {
    initialValue: null
  });
  readonly selectedElevationId = toSignal(
    this.elevationControl.valueChanges.pipe(startWith(this.elevationControl.value)),
    {
      initialValue: null
    }
  );
  readonly isSaveEnabled = computed(() => {
    const lotId = this.specLot()?.id;
    const modelId = this.selectedModelId();
    const elevationId = this.selectedElevationId();
    return !!(lotId && modelId && elevationId);
  });
  readonly saveTrigger = signal<{ lotId: string; modelId: string; elevationId: string } | null>(null);
  readonly saveResource = rxResourceFixed({
    params: () => this.saveTrigger(),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params) return of(null);
      return this.#salesService.updateSpecLot(params.lotId, params);
    }
  });
  readonly isSaving = computed(() => this.saveResource.isLoading());
  readonly elevationsResource = rxResourceFixed({
    params: () => ({
      modelId: this.selectedModelId(),
      specLot: this.specLot()
    }),
    defaultValue: [] as Elevation[],
    stream: ({ params }) => {
      if (!params.modelId) return of([] as Elevation[]);

      if (params.specLot?.selectedModel?.id === params.modelId && params.specLot.selectedModel.elevations?.length) {
        return of(params.specLot.selectedModel.elevations);
      }

      return this.#elevationService.getElevationByModelId(params.modelId).pipe(
        map((elevations: unknown) => {
          if (Array.isArray(elevations)) {
            return elevations as Elevation[];
          }
          if (elevations) {
            return [elevations as Elevation];
          }
          return [] as Elevation[];
        })
      );
    }
  });
  readonly elevationsDropdownData = computed(() => {
    const elevations = this.elevationsResource.value() as Elevation[] | null;
    if (!elevations || !Array.isArray(elevations)) return [];
    return elevations.map(elevation => ({
      id: elevation.id,
      firstLabel: elevation.elevationCode,
      secondLabel: elevation.description || elevation.elevationCode
    })) as DropdownData[];
  });

  readonly currentSelectedModel = computed(() => {
    const lot = this.specLot();
    return lot?.selectedModel ?? null;
  });

  readonly currentSelectedElevation = computed(() => {
    const lot = this.specLot();
    return lot?.selectedElevation ?? null;
  });

  constructor() {
    effect(() => {
      const specLot = this.specLot();
      const models = this.modelsDropdownData();

      if (!specLot || !models.length || this.modelControl.value) return;

      const modelId = specLot.selectedModel?.id || specLot.models?.[0]?.projectModelId;
      const matchingModel = models.find(m => m.id === modelId);

      if (matchingModel || models.length === 1) {
        this.modelControl.setValue(matchingModel?.id || models[0].id, { emitEvent: true });
      }
    });

    effect(() => {
      const specLot = this.specLot();
      const elevations = this.elevationsDropdownData();

      if (!specLot?.selectedElevation || !elevations.length || this.elevationControl.value) return;

      const matchingElevation = elevations.find(e => e.id === specLot.selectedElevation!.id);
      if (matchingElevation) {
        this.elevationControl.setValue(matchingElevation.id, { emitEvent: true });
      }
    });

    effect(() => {
      const modelId = this.selectedModelId();
      const elevations = this.elevationsDropdownData();
      const currentElevationId = this.elevationControl.value;

      if (modelId && currentElevationId && elevations.length) {
        const isValidElevation = elevations.some(e => e.id === currentElevationId);
        if (!isValidElevation) {
          this.elevationControl.setValue(null, { emitEvent: false });
        }
      }
    });

    effect(() => {
      const modelId = this.selectedModelId();
      if (modelId) {
        this.elevationControl.enable({ emitEvent: false });
      } else {
        this.elevationControl.disable({ emitEvent: false });
        this.elevationControl.setValue(null, { emitEvent: false });
      }
    });

    effect(() => {
      if (this.saveResource.hasValue() && this.saveResource.value() !== null) {
        const projectId = this.#route.snapshot.queryParamMap.get('projectId');
        projectId ? this.#router.navigate(['/sales/spec-lots'], { queryParams: { projectId } }) : this.saved.emit();
      }
    });

    effect(() => this.modelSelected.emit(this.selectedModelId()));
    effect(() => this.elevationSelected.emit(this.selectedElevationId()));
  }

  saveChanges(): void {
    const lotId = this.specLot()?.id;
    const modelId = this.selectedModelId();
    const elevationId = this.selectedElevationId();

    if (lotId && modelId && elevationId) {
      this.saveTrigger.set({ lotId, modelId, elevationId });
    }
  }

  isDisabled(): boolean {
    return this.modelControl.value && this.elevationControl.value ? true : false;
  }
}
